import { apiGet } from "./Api/apiCall";
import {
  ActionIdentifiersResponse,
  DocumentLabelsResponse,
  IdentifierTranslations,
  LabelOverviewProduct,
  LabelOverviewRow,
  LabelOverviewSortColumn,
  LabelOverviewSortDirection,
} from "../types/LabelOverview";

type LabelOverviewProductConfiguration = {
  key: LabelOverviewProduct;
  name: string;
};

export const labelOverviewProducts: LabelOverviewProductConfiguration[] = [
  { key: "common", name: "Produktübergreifend" },
  { key: "litigation", name: "Prozessprodukte" },
  { key: "settlement", name: "Kündigungsschutzklage" },
  { key: "traffic", name: "Ordnungswidrigkeit" },
  { key: "employmentContract", name: "Allgemeines Arbeitsrecht" },
  { key: "civilServiceLaw", name: "Beamtenrecht" },
  { key: "divorce", name: "Scheidung" },
];

const directProductKeys = new Set(
  labelOverviewProducts.filter(({ key }) => key !== "litigation").map(({ key }) => key),
);
const germanCollator = new Intl.Collator("de", { sensitivity: "base" });

const getLitigationLabels = (documentLabels: DocumentLabelsResponse): IdentifierTranslations => {
  const litigationLabels: IdentifierTranslations = {};

  Object.entries(documentLabels).forEach(([product, labels]) => {
    if (!directProductKeys.has(product as LabelOverviewProduct)) {
      Object.assign(litigationLabels, labels);
    }
  });

  return litigationLabels;
};

const getLabelsByProduct = (
  documentLabels: DocumentLabelsResponse,
  product: LabelOverviewProduct,
): IdentifierTranslations => {
  if (product === "litigation") {
    return getLitigationLabels(documentLabels);
  }

  return documentLabels[product] ?? {};
};

export const buildLabelOverviewRows = (
  documentLabels: DocumentLabelsResponse,
  actionIdentifiers: ActionIdentifiersResponse,
): LabelOverviewRow[] => {
  return labelOverviewProducts.flatMap(({ key: product, name: productName }) => {
    return Object.entries(getLabelsByProduct(documentLabels, product))
      .filter(([labelCode]) => labelCode !== "NO_LABEL")
      .map(([labelCode, labelName]) => {
        const sortedActionIdentifiers = Object.entries(actionIdentifiers[labelCode] ?? {})
          .map(([code, name]) => ({ code, name }))
          .sort((first, second) => {
            return germanCollator.compare(first.name, second.name) || germanCollator.compare(first.code, second.code);
          });

        return {
          product,
          productName,
          labelCode,
          labelName,
          actionIdentifiers: sortedActionIdentifiers,
        };
      });
  });
};

export const sortLabelOverviewRows = (
  rows: LabelOverviewRow[],
  sortColumn: LabelOverviewSortColumn,
  sortDirection: LabelOverviewSortDirection,
): LabelOverviewRow[] => {
  const direction = sortDirection === "asc" ? 1 : -1;

  return [...rows].sort((first, second) => {
    if (sortColumn === "product") {
      const productComparison = germanCollator.compare(first.productName, second.productName) * direction;
      if (productComparison !== 0) {
        return productComparison;
      }

      return (
        germanCollator.compare(first.labelName, second.labelName) ||
        germanCollator.compare(first.labelCode, second.labelCode)
      );
    }

    const labelComparison = germanCollator.compare(first.labelName, second.labelName) * direction;
    if (labelComparison !== 0) {
      return labelComparison;
    }

    return (
      germanCollator.compare(first.productName, second.productName) ||
      germanCollator.compare(first.labelCode, second.labelCode)
    );
  });
};

export const fetchLabelOverviewRows = async (): Promise<LabelOverviewRow[]> => {
  const [documentLabels, actionIdentifiers] = await Promise.all([
    apiGet<DocumentLabelsResponse>("lb/services/document_labels"),
    apiGet<ActionIdentifiersResponse>("lb/services/action_identifiers"),
  ]);

  return buildLabelOverviewRows(documentLabels, actionIdentifiers);
};
